import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { navLinks } from '@/mocks/donnos';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (location.pathname !== '/' || !location.hash) return;

    window.setTimeout(() => {
      const target = document.querySelector(location.hash);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }, [location.pathname, location.hash]);

  const closeMenus = () => {
    setMobileOpen(false);
    setDropdownOpen(false);
  };

  const scrollToHash = (hash: string) => {
    window.setTimeout(() => {
      const target = document.querySelector(hash);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 0);
  };

  const handleNavClick = (e: React.MouseEvent, href: string) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      closeMenus();

      if (location.pathname === '/') {
        if (location.hash !== href) {
          navigate(`/${href}`);
        }
        scrollToHash(href);
        return;
      }

      navigate(`/${href}`);
      return;
    }

    if (href.startsWith('/')) {
      e.preventDefault();
      navigate(href);
      window.setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 0);
      closeMenus();
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'border-b border-white/40 bg-white/80 py-3 shadow-md backdrop-blur-xl'
          : 'border-b border-white/10 bg-white/10 py-5 shadow-sm backdrop-blur-xl'
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="grid grid-cols-[auto_auto] items-center justify-between md:grid-cols-[auto_1fr_auto]">
          <a href="/#inicio" onClick={(e) => handleNavClick(e, '#inicio')} className="flex items-center">
            <span className="flex h-16 w-[118px] items-center justify-center overflow-hidden rounded-sm transition-all duration-300">
              <img
                src={scrolled ? '/assets/donnos-logo-scroll.png' : '/assets/donnos-logo.png'}
                alt="Donnos - Vendas e Serviços Integrados"
                className="h-full w-full object-contain"
              />
            </span>
          </a>

          <div className="hidden md:flex items-center justify-center gap-1">
            {navLinks.map((link) => (
              <div
                key={link.href}
                className="relative"
                onMouseEnter={() => link.hasDropdown && setDropdownOpen(true)}
                onMouseLeave={() => link.hasDropdown && setDropdownOpen(false)}
              >
                <a
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    scrolled ? 'text-gray-700 hover:text-donnos-green' : 'text-white/90 hover:text-white'
                  }`}
                >
                  {link.label}
                  {link.hasDropdown && <i className="ri-arrow-down-s-line ml-1"></i>}
                </a>
                {link.hasDropdown && dropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-xl border border-gray-100 py-2">
                    <a href="/produtos" onClick={(e) => handleNavClick(e, '/produtos')} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-donnos-green">Computadores e Notebooks</a>
                    <a href="/produtos" onClick={(e) => handleNavClick(e, '/produtos')} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-donnos-green">Impressoras e Suprimentos</a>
                    <a href="/produtos" onClick={(e) => handleNavClick(e, '/produtos')} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-donnos-green">Periféricos e Acessórios</a>
                    <a href="/produtos" onClick={(e) => handleNavClick(e, '/produtos')} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-donnos-green">Infraestrutura de TI</a>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="hidden w-[118px] md:block" aria-hidden="true" />

          <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden p-2" aria-label="Abrir menu">
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl ${scrolled ? 'text-gray-900' : 'text-white'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-white shadow-lg border-t border-gray-100">
          <div className="p-4 space-y-2">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="block px-4 py-3 text-gray-700 hover:text-donnos-green hover:bg-gray-50 rounded-lg font-medium"
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}
